import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Button } from '@/components/ui/button';
import { Model3DStats } from './types';
import { RotateCw, Eye, Box, RefreshCw, Upload, Palette } from 'lucide-react';

interface Model3DViewerProps {
  object3D: THREE.Object3D | null;
  stats: Model3DStats | null;
  filamentColorHex?: string;
  onGoToAnalyzer: () => void;
}

export default function Model3DViewer({
  object3D,
  stats,
  filamentColorHex = '#d97706',
  onGoToAnalyzer,
}: Model3DViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const modelGroupRef = useRef<THREE.Group | null>(null);
  const animFrameIdRef = useRef<number | null>(null);

  const [isWireframe, setIsWireframe] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [activeColor, setActiveColor] = useState(filamentColorHex);

  // Sync color when filament color changes
  useEffect(() => {
    if (filamentColorHex) {
      setActiveColor(filamentColorHex);
    }
  }, [filamentColorHex]);

  // Update materials when color or wireframe changes
  useEffect(() => {
    if (!modelGroupRef.current) return;

    modelGroupRef.current.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        if (mesh.material) {
          const mat = mesh.material as THREE.MeshStandardMaterial;
          mat.wireframe = isWireframe;
          mat.color.set(activeColor);
          mat.roughness = 0.4;
          mat.metalness = 0.15;
          mat.needsUpdate = true;
        }
      }
    });
  }, [activeColor, isWireframe]);

  // Controls auto-rotate
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.autoRotate = autoRotate;
      controlsRef.current.autoRotateSpeed = 2.0;
    }
  }, [autoRotate]);

  // Setup Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;

    const width = containerRef.current.clientWidth || 500;
    const height = containerRef.current.clientHeight || 450;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000);
    camera.position.set(180, 180, 220);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;

    // Clear old canvases if any
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxPolarAngle = Math.PI / 2 + 0.05; // Do not go completely underneath the build plate
    controlsRef.current = controls;

    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.9);
    scene.add(ambientLight);

    // Directional Key light
    const dirLight1 = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight1.position.set(150, 300, 200);
    dirLight1.castShadow = true;
    dirLight1.shadow.mapSize.width = 1024;
    dirLight1.shadow.mapSize.height = 1024;
    scene.add(dirLight1);

    // Fill light
    const dirLight2 = new THREE.DirectionalLight(0xfff7ed, 0.6);
    dirLight2.position.set(-150, 100, -150);
    scene.add(dirLight2);

    // Build plate / Print Bed (220 x 220 mm)
    const bedGroup = new THREE.Group();
    const bedSize = 220;
    const bedGeo = new THREE.PlaneGeometry(bedSize, bedSize);
    const bedMat = new THREE.MeshStandardMaterial({
      color: 0xe5e7eb,
      roughness: 0.8,
      metalness: 0.1,
    });
    const bedMesh = new THREE.Mesh(bedGeo, bedMat);
    bedMesh.rotation.x = -Math.PI / 2;
    bedMesh.position.y = -0.1;
    bedMesh.receiveShadow = true;
    bedGroup.add(bedMesh);

    // Millimeter Grid
    const gridHelper = new THREE.GridHelper(bedSize, 22, 0x9ca3af, 0xd1d5db);
    gridHelper.position.y = 0;
    bedGroup.add(gridHelper);

    // Build plate borders
    const edgesGeo = new THREE.EdgesGeometry(bedGeo);
    const edgesMat = new THREE.LineBasicMaterial({ color: 0x94a3b8, linewidth: 2 });
    const edgesLine = new THREE.LineSegments(edgesGeo, edgesMat);
    edgesLine.rotation.x = -Math.PI / 2;
    bedGroup.add(edgesLine);

    scene.add(bedGroup);

    // Group for model
    const modelGroup = new THREE.Group();
    modelGroupRef.current = modelGroup;
    scene.add(modelGroup);

    // Render loop
    const animate = () => {
      animFrameIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize observer
    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
      const newWidth = containerRef.current.clientWidth;
      const newHeight = containerRef.current.clientHeight;
      if (newWidth > 0 && newHeight > 0) {
        cameraRef.current.aspect = newWidth / newHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(newWidth, newHeight);
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      if (animFrameIdRef.current) cancelAnimationFrame(animFrameIdRef.current);
      resizeObserver.disconnect();
      renderer.dispose();
    };
  }, []);

  // Update object in scene
  useEffect(() => {
    if (!sceneRef.current || !modelGroupRef.current) return;

    // Clear previous model
    while (modelGroupRef.current.children.length > 0) {
      const obj = modelGroupRef.current.children[0];
      modelGroupRef.current.remove(obj);
    }

    if (!object3D) return;

    // Clone or add object
    const cloned = object3D.clone();

    // Apply materials
    cloned.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(activeColor),
          roughness: 0.35,
          metalness: 0.15,
          wireframe: isWireframe,
        });
      }
    });

    // Center and place on build plate
    const bbox = new THREE.Box3().setFromObject(cloned);
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    bbox.getCenter(center);
    bbox.getSize(size);

    // Move so that bottom is at y=0, and centered in X & Z
    cloned.position.x = -center.x;
    cloned.position.y = -bbox.min.y;
    cloned.position.z = -center.z;

    modelGroupRef.current.add(cloned);

    // Adjust camera to fit model nicely
    if (cameraRef.current && controlsRef.current) {
      const maxDim = Math.max(size.x, size.y, size.z, 50);
      const dist = maxDim * 2.2;
      cameraRef.current.position.set(dist * 0.9, dist * 0.9, dist * 1.1);
      controlsRef.current.target.set(0, size.y / 2, 0);
      controlsRef.current.update();
    }
  }, [object3D]);

  const handleResetCamera = () => {
    if (!cameraRef.current || !controlsRef.current || !modelGroupRef.current) return;
    const bbox = new THREE.Box3().setFromObject(modelGroupRef.current);
    const size = new THREE.Vector3();
    bbox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z, 60);
    const dist = maxDim * 2.2;
    cameraRef.current.position.set(dist * 0.9, dist * 0.9, dist * 1.1);
    controlsRef.current.target.set(0, size.y / 2 || 15, 0);
    controlsRef.current.update();
  };

  const presetColors = [
    { name: 'Terracota Mila', hex: '#c2410c' },
    { name: 'Grafito', hex: '#262626' },
    { name: 'Blanco Nieve', hex: '#f9fafb' },
    { name: 'Sage Green', hex: '#4d7c0f' },
    { name: 'Azul Eléctrico', hex: '#2563eb' },
    { name: 'Amarillo Mostaza', hex: '#eab308' },
  ];

  return (
    <div className="relative w-full h-[520px] rounded-2xl overflow-hidden border border-border/80 bg-stone-50/60 dark:bg-stone-900/40 shadow-inner flex flex-col">
      {/* 3D Canvas Container */}
      <div ref={containerRef} className="w-full h-full cursor-grab active:cursor-grabbing" />

      {/* Empty State Overlay */}
      {!object3D && (
        <div className="absolute inset-0 bg-background/85 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center z-10">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 text-primary shadow-sm animate-pulse">
            <Box className="w-8 h-8" />
          </div>

          <h3 className="text-xl font-bold text-foreground mb-1">
            Todavía no cargaste ningún modelo
          </h3>

          <p className="text-sm font-medium text-primary/90 max-w-md mb-3">
            Mirá tu archivo en 3D antes de cotizar: giralo, acercalo y chequeá que sea la pieza que pensabas.
          </p>

          <p className="text-xs text-muted-foreground max-w-lg mb-6 leading-relaxed">
            El visor no pide el archivo aparte: lo toma del <strong>Analizador de modelo 3D</strong>, la ficha de acá arriba. Cargá ahí tu STL, OBJ o 3MF y la pieza aparece sola en esta ficha.
          </p>

          <Button
            onClick={onGoToAnalyzer}
            className="gap-2 shadow-sm font-semibold px-6"
          >
            <Upload className="w-4 h-4" />
            Ir al analizador y cargar el archivo
          </Button>
        </div>
      )}

      {/* Floating Toolbar when model is loaded */}
      {object3D && stats && (
        <>
          {/* Top Bar: Model Name & Dimensions */}
          <div className="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none z-10">
            <div className="pointer-events-auto bg-background/90 backdrop-blur-md border border-border px-3.5 py-1.5 rounded-xl shadow-sm text-xs flex items-center gap-2">
              <span className="font-bold text-foreground max-w-[200px] truncate">{stats.fileName}</span>
              <span className="text-muted-foreground">|</span>
              <span className="text-primary font-mono font-medium">
                {stats.dimensions.x} × {stats.dimensions.y} × {stats.dimensions.z} mm
              </span>
            </div>

            {/* Quick Actions */}
            <div className="pointer-events-auto flex items-center gap-1.5 bg-background/90 backdrop-blur-md border border-border p-1 rounded-xl shadow-sm">
              <Button
                variant={autoRotate ? "default" : "ghost"}
                size="icon"
                className="h-8 w-8 rounded-lg"
                onClick={() => setAutoRotate(!autoRotate)}
                title="Giro automático 360°"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
              <Button
                variant={isWireframe ? "default" : "ghost"}
                size="icon"
                className="h-8 w-8 rounded-lg"
                onClick={() => setIsWireframe(!isWireframe)}
                title="Modo Malla (Wireframe)"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg"
                onClick={handleResetCamera}
                title="Reiniciar cámara"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Bottom Controls Bar: Color selection & Bed reference */}
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none z-10">
            {/* Color Swatches */}
            <div className="pointer-events-auto flex items-center gap-1.5 bg-background/90 backdrop-blur-md border border-border px-3 py-1.5 rounded-xl shadow-sm">
              <Palette className="w-3.5 h-3.5 text-muted-foreground mr-1" />
              <div className="flex items-center gap-1">
                {presetColors.map((c) => (
                  <button
                    key={c.hex}
                    onClick={() => setActiveColor(c.hex)}
                    title={c.name}
                    className={`w-5 h-5 rounded-full border transition-all ${
                      activeColor.toLowerCase() === c.hex.toLowerCase()
                        ? 'ring-2 ring-primary scale-110'
                        : 'opacity-85 hover:opacity-100 hover:scale-105'
                    }`}
                    style={{ backgroundColor: c.hex }}
                  />
                ))}
              </div>
            </div>

            {/* Cama info */}
            <div className="pointer-events-auto bg-background/90 backdrop-blur-md border border-border px-3 py-1 rounded-xl shadow-sm text-[11px] text-muted-foreground">
              Cama: <span className="font-medium text-foreground">220 × 220 mm</span> • Botón izq: Rotar • Rueda: Zoom • Botón der: Mover
            </div>
          </div>
        </>
      )}
    </div>
  );
}
